import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { SPHERE_TUNING } from './NetworkSphere';

/**
 * SphereWidgets — small glassmorphism cards orbiting the sphere, each
 * representing a stage in a BI analytics pipeline.  One card spawns at a
 * time (cadence depends on hover state), fades in, displays a stage label
 * with an animated mini progress bar, and fades out.
 */

// ─── Pipeline stages (data, not rendering) ─────────────────────────────────
export const PIPELINE_STAGES = [
  { key: 'INGEST',    label: 'Ingesting data',     hint: 'streams · csv · api',     accent: '#60a5fa' },
  { key: 'CLEAN',     label: 'Cleaning',           hint: 'nulls · types · dupes',    accent: '#a78bfa' },
  { key: 'MODEL',     label: 'Modeling',           hint: 'joins · aggregates',       accent: '#22d3ee' },
  { key: 'DETECT',    label: 'Detecting patterns', hint: 'anomalies · trends',       accent: '#34d399' },
  { key: 'FORECAST',  label: 'Forecasting',        hint: 'arima · holt-winters',     accent: '#facc15' },
  { key: 'INSIGHT',   label: 'Generating insights',hint: 'segments · drivers',       accent: '#f472b6' },
  { key: 'DASHBOARD', label: 'Dashboard ready',    hint: 'live · published',         accent: '#4ade80' },
];

// Static fallback anchor used only by the reduced-motion / no-tracking
// path. The interactive path positions widgets imperatively each frame
// based on the current 2D-projected position of an attached sphere node.
export const WIDGET_FALLBACK_ANCHOR = { top: '20%', left: '88%' };

// ─── Tunables ──────────────────────────────────────────────────────────────
export const WIDGET_TUNING = {
  baseGapMs: 11000,       // default cadence between widget spawns
  hoverGapMs: 8000,       // faster while hovering sphere
  lifeMs: 7000,           // how long each widget stays visible
};

// ─── Animations ────────────────────────────────────────────────────────────
// fadeSlide animates the inner Card. Because the outer wrapper handles
// the moving anchor (translate to current node position) imperatively
// each frame, the Card's transform is free to handle fade-in/out and
// the visual centering on the anchor (translate(-50%, -50%)).
const fadeSlide = keyframes`
  0%   { opacity: 0; transform: translate(-50%, calc(-50% + 6px)) scale(0.96); }
  12%  { opacity: 1; transform: translate(-50%, -50%)              scale(1); }
  88%  { opacity: 1; transform: translate(-50%, -50%)              scale(1); }
  100% { opacity: 0; transform: translate(-50%, calc(-50% - 4px)) scale(0.98); }
`;

const fillBar = keyframes`
  from { width: 0%; }
  to   { width: 100%; }
`;

const pulseDot = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50%      { transform: scale(1.3); opacity: 0.7; }
`;

// ─── Styled card ───────────────────────────────────────────────────────────
// CardOuter is the moving anchor — its transform is updated imperatively
// each frame to follow a chosen sphere node's projected 2D position.
const CardOuter = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  pointer-events: none;
  z-index: 4;
  will-change: transform;
  /* Render off-screen until the RAF tick has a position to write */
  transform: translate(-9999px, -9999px);

  @media (max-width: 900px) {
    display: none;
  }
`;

// Card is the visible chip — fixed width so the centering translate(-50%, -50%)
// inside fadeSlide produces a stable visual anchor on the moving node.
const Card = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 130px;
  padding: 0.4rem 0.55rem 0.45rem;
  background: rgb(var(--bg) / 0.55);
  backdrop-filter: blur(14px) saturate(1.2);
  -webkit-backdrop-filter: blur(14px) saturate(1.2);
  border: 1px solid rgb(var(--fg) / 0.08);
  border-radius: 9px;
  font-family: 'JetBrains Mono', 'SF Mono', Menlo, monospace;
  color: rgb(var(--fg) / 0.78);
  font-size: 0.58rem;
  letter-spacing: 0.04em;
  box-shadow:
    0 8px 22px -10px rgb(0 0 0 / calc(0.6 * var(--shadow-mul))),
    inset 0 1px 0 rgb(var(--fg) / 0.04),
    0 0 0 1px rgb(var(--fg) / 0.02);
  animation: ${fadeSlide} var(--life-ms) cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;

  & .row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.4rem;
    margin-bottom: 0.32rem;
  }
  & .stage {
    display: inline-flex;
    align-items: center;
    gap: 0.32rem;
    text-transform: uppercase;
    letter-spacing: 0.18em;
    font-size: 0.5rem;
    color: rgb(var(--fg) / 0.55);
  }
  & .dot {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: var(--accent);
    box-shadow: 0 0 6px var(--accent-glow);
    animation: ${pulseDot} 1.4s ease-in-out infinite;
  }
  & .pct {
    color: rgb(var(--fg) / 0.45);
    font-size: 0.54rem;
  }
  & .label {
    color: rgb(var(--fg) / 0.92);
    font-size: 0.68rem;
    letter-spacing: 0.01em;
    margin-bottom: 0.18rem;
  }
  & .hint {
    color: rgb(var(--fg) / 0.4);
    font-size: 0.52rem;
    letter-spacing: 0.06em;
    margin-bottom: 0.4rem;
  }
  & .track {
    width: 100%;
    height: 2px;
    background: rgb(var(--fg) / 0.07);
    border-radius: 999px;
    overflow: hidden;
  }
  & .fill {
    height: 100%;
    background: linear-gradient(
      90deg,
      rgb(var(--fg) / 0.2),
      var(--accent) 60%,
      rgb(var(--fg) / 0.95)
    );
    animation: ${fillBar} calc(var(--life-ms) * 0.72) ease-out forwards;
  }
`;

// hex → rgba helper for accent glow
const hexToRgba = (hex, a) => {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
};

const SphereWidgets = ({
  enabled = true,
  hoverActiveRef,
  reducedMotion = false,
  // Ref populated by NetworkSphere each frame: positions[i] = {x, y, z}
  // in CSS pixels relative to the sphere canvas. Widgets pick a random
  // node index at spawn and ride along with that node's projection.
  nodePositionsRef,
}) => {
  const [active, setActive] = useState([]);
  const stageIdx = useRef(0);
  const idCounter = useRef(0);
  // Maps widget id → DOM element of CardOuter, populated by callback refs
  // so the tracking RAF can reach the latest mounted nodes without state.
  const widgetEls = useRef(new Map());

  useEffect(() => {
    if (!enabled) return undefined;

    // Reduced motion: render a single static widget at the fallback anchor.
    if (reducedMotion) {
      const stage = PIPELINE_STAGES[5]; // "Generating insights"
      setActive([{
        id: 'static',
        stage,
        nodeIdx: -1, // sentinel: don't track
        lifeMs: 9_999_999,
        pct: 88,
      }]);
      return undefined;
    }

    let cancelled = false;
    let nextTimer;

    const spawn = () => {
      if (cancelled) return;
      const stage = PIPELINE_STAGES[stageIdx.current % PIPELINE_STAGES.length];
      stageIdx.current += 1;
      const id = ++idCounter.current;
      const pct = 60 + Math.floor(Math.random() * 40);
      const nodeIdx = Math.floor(Math.random() * SPHERE_TUNING.nodeCount);
      const widget = { id, stage, nodeIdx, lifeMs: WIDGET_TUNING.lifeMs, pct };
      setActive([widget]);
      setTimeout(() => {
        if (cancelled) return;
        setActive((curr) => curr.filter((w) => w.id !== id));
      }, WIDGET_TUNING.lifeMs);

      const gap = (hoverActiveRef && hoverActiveRef.current)
        ? WIDGET_TUNING.hoverGapMs
        : WIDGET_TUNING.baseGapMs;
      nextTimer = setTimeout(spawn, gap);
    };

    nextTimer = setTimeout(spawn, 900);

    return () => {
      cancelled = true;
      if (nextTimer) clearTimeout(nextTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, reducedMotion]);

  // Tracking RAF: update each active widget's CardOuter transform from
  // the latest projected node positions. Reads dataset.nodeIdx so it
  // doesn't need to know the React state shape.
  useEffect(() => {
    if (!enabled || reducedMotion) return undefined;
    if (!nodePositionsRef) return undefined;
    let raf;
    const tick = () => {
      raf = requestAnimationFrame(tick);
      const positions = nodePositionsRef.current;
      if (!positions || !positions.length) return;
      widgetEls.current.forEach((el) => {
        const idx = parseInt(el.dataset.nodeIdx, 10);
        if (Number.isNaN(idx)) return;
        const pos = positions[idx];
        if (!pos) return;
        el.style.transform = `translate(${pos.x}px, ${pos.y}px)`;
      });
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [enabled, reducedMotion, nodePositionsRef]);

  const setEl = (id, nodeIdx) => (el) => {
    if (el) {
      el.dataset.nodeIdx = String(nodeIdx);
      widgetEls.current.set(id, el);
    } else {
      widgetEls.current.delete(id);
    }
  };

  return (
    <>
      {active.map((w) => {
        const isStatic = w.nodeIdx < 0;
        const cardStyle = {
          '--life-ms': `${w.lifeMs}ms`,
          '--accent': w.stage.accent,
          '--accent-glow': hexToRgba(w.stage.accent, 0.55),
        };
        const cardContent = (
          <>
            <div className="row">
              <span className="stage">
                <span className="dot" />
                {w.stage.key}
              </span>
              <span className="pct">{w.pct}%</span>
            </div>
            <div className="label">{w.stage.label}</div>
            <div className="hint">{w.stage.hint}</div>
            <div className="track"><div className="fill" /></div>
          </>
        );

        if (isStatic) {
          // Static widget (reduced motion) — uses fallback anchor, no tracking.
          return (
            <Card
              key={w.id}
              style={{
                ...cardStyle,
                top: WIDGET_FALLBACK_ANCHOR.top,
                left: WIDGET_FALLBACK_ANCHOR.left,
                animation: 'none',
                transform: 'translate(-50%, -50%)',
              }}
            >
              {cardContent}
            </Card>
          );
        }

        return (
          <CardOuter key={w.id} ref={setEl(w.id, w.nodeIdx)}>
            <Card style={cardStyle}>{cardContent}</Card>
          </CardOuter>
        );
      })}
    </>
  );
};

export default SphereWidgets;
